import { JsonObject, JsonValue } from "@/domains/common/types";
import { _compactStringForLogging } from "@/modules/string/compact-for-logging";

type CompactObjectForLoggingOptions = {
  maxArrayItems?: number;
  maxDepth?: number;
  maxKeys?: number;
  maxStringLength?: number;
};

const DEFAULT_MAX_ARRAY_ITEMS = 8;
const DEFAULT_MAX_DEPTH = 3;
const DEFAULT_MAX_KEYS = 25;
const DEFAULT_MAX_STRING_LENGTH = 200;

const SENSITIVE_KEY_PATTERN =
  /(authorization|cookie|token|secret|password|passwd|pwd|signature|signed|api[_-]?key|access[_-]?key|auth|code)/i;

const normalizePositiveInteger = (value: number | undefined, fallback: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
};

const safeStringify = (value: unknown) => {
  try {
    const serialized = JSON.stringify(value);
    return typeof serialized === "string" ? serialized : String(value);
  } catch {
    return String(value);
  }
};

const getValueType = (value: unknown): string => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (value instanceof Error) return "error";
  return typeof value;
};

const toJsonValue = (
  value: unknown,
  options: Required<CompactObjectForLoggingOptions>,
  depth: number,
  seen: WeakSet<object>
): JsonValue => {
  if (value === null) return null;

  if (typeof value === "string") {
    if (value.length <= options.maxStringLength) return value;
    return _compactStringForLogging(value, { maxLength: options.maxStringLength }) as JsonObject;
  }

  if (typeof value === "number" || typeof value === "boolean") return value;

  if (typeof value === "undefined") return "[undefined]";
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "symbol") return value.toString();
  if (typeof value === "function") return "[function]";

  if (value instanceof Error) {
    return _compactErrorForLogging(value, options) as JsonObject;
  }

  if (typeof value !== "object") {
    return String(value);
  }

  if (seen.has(value)) {
    return "[circular]";
  }

  if (depth >= options.maxDepth) {
    return _summarizeObjectForLogging(value) as JsonObject;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    const items = value
      .slice(0, options.maxArrayItems)
      .map(item => toJsonValue(item, options, depth + 1, seen));
    if (value.length > options.maxArrayItems) {
      items.push(`[omitted ${value.length - options.maxArrayItems} items]`);
    }
    return items;
  }

  const entries = Object.entries(value);
  const result: JsonObject = {};

  for (const [key, item] of entries.slice(0, options.maxKeys)) {
    result[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? "[redacted]"
      : toJsonValue(item, options, depth + 1, seen);
  }

  if (entries.length > options.maxKeys) {
    result.__omittedKeyCount = entries.length - options.maxKeys;
  }

  return result;
};

const normalizeCompactObjectOptions = (
  options: CompactObjectForLoggingOptions = {}
): Required<CompactObjectForLoggingOptions> => {
  return {
    maxArrayItems: normalizePositiveInteger(options.maxArrayItems, DEFAULT_MAX_ARRAY_ITEMS),
    maxDepth: normalizePositiveInteger(options.maxDepth, DEFAULT_MAX_DEPTH),
    maxKeys: normalizePositiveInteger(options.maxKeys, DEFAULT_MAX_KEYS),
    maxStringLength: normalizePositiveInteger(options.maxStringLength, DEFAULT_MAX_STRING_LENGTH),
  };
};

export const _compactObjectForLogging = (
  value: unknown,
  options: CompactObjectForLoggingOptions = {}
): JsonValue => {
  return toJsonValue(value, normalizeCompactObjectOptions(options), 0, new WeakSet<object>());
};

export const _summarizeObjectForLogging = (value: unknown): JsonObject => {
  const summary: JsonObject = {
    present: value !== undefined && value !== null,
    type: getValueType(value),
  };

  if (typeof value === "string") {
    const compactString = _compactStringForLogging(value);
    return {
      ...summary,
      hash: compactString.hash,
      length: compactString.length,
      preview: compactString.preview,
      truncated: compactString.truncated,
    };
  }

  if (Array.isArray(value)) {
    return {
      ...summary,
      length: value.length,
    };
  }

  if (typeof value === "object" && value !== null) {
    const keys = Object.keys(value);
    const serialized = safeStringify(value);
    const compactSerialized = _compactStringForLogging(serialized, { maxLength: 120 });

    return {
      ...summary,
      hash: compactSerialized.hash,
      jsonLength: serialized.length,
      keyCount: keys.length,
      keys: keys.slice(0, 20),
      omittedKeyCount: Math.max(0, keys.length - 20),
    };
  }

  return summary;
};

export const _compactErrorForLogging = (
  error: unknown,
  options: CompactObjectForLoggingOptions = {}
): JsonObject => {
  const compactOptions = normalizeCompactObjectOptions(options);

  if (!(error instanceof Error)) {
    return {
      message: _compactStringForLogging(String(error), {
        maxLength: compactOptions.maxStringLength,
      }) as JsonObject,
      type: getValueType(error),
    };
  }

  const response = (error as Error & { response?: unknown }).response;

  const result: JsonObject = {
    name: error.name,
    message: _compactStringForLogging(error.message, {
      maxLength: compactOptions.maxStringLength,
    }) as JsonObject,
    stack: error.stack
      ? (_compactStringForLogging(error.stack, {
          maxLength: Math.max(compactOptions.maxStringLength, 500),
        }) as JsonObject)
      : null,
  };

  if (response !== undefined) {
    result.response = _summarizeObjectForLogging(response);
  }

  return result;
};
